import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import useCalculateTax from "../hooks/useCalculateTax";
import useCreateQuote from "../hooks/useCreateQuote";
import useGetShippingMethods from "../hooks/useGetShippingMethods";
import useGetStorefronts from "../hooks/useGetStorefronts";
import type { QuoteAddressOption } from "../hooks/useQuoteAddresses";
import { EMPTY_SHIPPING_ADDRESS, type ComposedLine, type DraftLine, type ShippingAddressDraft } from "./types";

/** A line to seed the composer with, paired with its starting quantity (e.g. a cart line). */
export interface SeedLine {
  line: DraftLine;
  quantity: number;
}

/** Stable business identity for a shipping method (carrier + service level). The backend may hand
 * back fresh `id`s on each freight preview, so selection is preserved against this instead. */
function methodKey(method: { carrier?: string; shippingType?: string }): string {
  return `${method.carrier ?? ""}|${method.shippingType ?? ""}`;
}

function useClaimNumber(claim: "orgUnitIdCurrent" | "compositeCatalogId"): number | null {
  const { getClaimValue } = useClaims();
  return useMemo(() => {
    const parsed = Number(getClaimValue(claim));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [getClaimValue, claim]);
}

/**
 * Shared quote-composer state for both the standalone create page (catalog search seeds lines)
 * and the cart-fed Save Quote dialog (cart seeds lines). Owns line/quantity, ship-to, shipping
 * method, email, and name/description state plus the create/shipping mutations and every derived
 * value the steps and summary need. Product sourcing (search vs cart) stays with the host.
 */
export default function useQuoteComposerState({
  allowZeroQuantity = false,
}: { allowZeroQuantity?: boolean } = {}) {
  const orgUnitId = useClaimNumber("orgUnitIdCurrent");
  const { getClaimValue } = useClaims();
  const userEmail = getClaimValue("userEmail") ?? undefined;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedOrgUnitId, setSelectedOrgUnitId] = useState<number | "">("");
  const [composedLines, setComposedLines] = useState<ComposedLine[]>([]);
  // Monotonic client-side row id so duplicate products stay independent rows across edits.
  const nextLineId = useRef(1);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddressDraft>(EMPTY_SHIPPING_ADDRESS);
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState<number | null>(null);
  // The user's method pick tracked by its stable carrier+service identity, so it survives freight
  // re-previews (which may renumber ids) and lets shipping — and therefore tax — re-estimate in place.
  const selectedMethodKeyRef = useRef<string | null>(null);
  const [selectedShippingAddressId, setSelectedShippingAddressId] = useState<number | null>(null);
  // "Include Shipping" gates the ship-to + method section (checked by default). Lifted here so the
  // choice persists across step unmount/remount on the paged create page.
  const [includeShipping, setIncludeShipping] = useState(true);
  const [includeTax, setIncludeTax] = useState(true);
  // Email-on-create: two independent opt-ins matching the design — copy to self, and/or copy to
  // others (which reveals the recipients + message fields).
  const [sendCopyToSelf, setSendCopyToSelf] = useState(false);
  const [sendToOthers, setSendToOthers] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [emailMessage, setEmailMessage] = useState("");
  // Optional currency id; null => backend uses its default (USD). Internal create page only.
  const [currencyId, setCurrencyId] = useState<number | null>(null);

  const { data: storefronts, isLoading: storefrontsLoading } = useGetStorefronts();
  const { enabled: createQuotesEnabled } = useFeatureFlag("spu.create-quotes", false);

  const createMutation = useCreateQuote();
  const shippingMethodsMutation = useGetShippingMethods();
  const taxMutation = useCalculateTax();

  // Default to the current org unit once storefronts load.
  useEffect(() => {
    if (storefronts && storefronts.length > 0 && selectedOrgUnitId === "") {
      const match = storefronts.find((s) => s.orgUnitId === orgUnitId);
      setSelectedOrgUnitId(match ? match.orgUnitId! : storefronts[0].orgUnitId!);
    }
  }, [storefronts, orgUnitId, selectedOrgUnitId]);

  // Reconcile the selected method against every fresh preview: re-select the same carrier+service the
  // user chose (its freight just updates for the new weight/quantities, so tax re-estimates too), and
  // drop the pick only when that method is no longer offered. Left untouched while a preview is
  // in-flight (data momentarily undefined) so the selection does not flicker; the pending indicator
  // covers the gap and other handlers clear it when shipping is turned off or the address changes.
  const shippingMethodsData = shippingMethodsMutation.data;
  useEffect(() => {
    if (!shippingMethodsData) return;
    const key = selectedMethodKeyRef.current;
    const match = key != null ? shippingMethodsData.find((m) => methodKey(m) === key) : undefined;
    setSelectedShippingMethodId(match?.id ?? null);
    if (!match) selectedMethodKeyRef.current = null;
  }, [shippingMethodsData]);

  // Record the picked method's stable identity so a later freight re-preview can re-select it.
  const handleSelectShippingMethod = useCallback((id: number | null) => {
    setSelectedShippingMethodId(id);
    const match = id != null ? (shippingMethodsMutation.data ?? []).find((m) => m.id === id) : undefined;
    selectedMethodKeyRef.current = match ? methodKey(match) : null;
  }, [shippingMethodsMutation.data]);

  const addLine = useCallback((line: DraftLine, quantity = 1) => {
    // Catalog search keeps one row per product: bump the quantity if the product is already present,
    // otherwise append a new independent row. Shipping methods re-fetch reactively (see the effect
    // below), so the picker refreshes freight instead of blanking on every edit.
    setComposedLines((prev) => {
      const existing = prev.find((l) => l.product.productId === line.productId);
      if (existing) {
        return prev.map((l) => (l.id === existing.id ? { ...l, quantity: l.quantity + quantity } : l));
      }
      return [...prev, { id: nextLineId.current++, product: line, quantity }];
    });
  }, []);

  const removeLine = useCallback((id: number) => {
    setComposedLines((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const setQuantity = useCallback((id: number, qty: number) => {
    if (qty <= 0) {
      // Cart-fed flows opt into a friendly reversible exclude: keep the row at 0 (visible but
      // excluded) so the user can tick it back up. Catalog search leaves this off and removes at 0.
      if (allowZeroQuantity) {
        setComposedLines((prev) => prev.map((l) => (l.id === id ? { ...l, quantity: 0 } : l)));
        return;
      }
      removeLine(id);
      return;
    }
    setComposedLines((prev) => prev.map((l) => (l.id === id ? { ...l, quantity: qty } : l)));
  }, [allowZeroQuantity, removeLine]);

  /** Replace the composed lines wholesale (used by the dialog to seed from the cart on open). Each seed
   * becomes its own row so duplicate cart lines of the same product stay independently editable. */
  const seedLines = useCallback((seed: SeedLine[]) => {
    setComposedLines(seed.map((s) => ({ id: nextLineId.current++, product: s.line, quantity: s.quantity })));
  }, []);

  // Clears every user-entered field so a reused (still-mounted) composer starts clean, e.g. the
  // Save-as-Quote dialog reopening on a fresh cart snapshot. Storefront selection is left intact.
  const resetComposer = useCallback(() => {
    setName("");
    setDescription("");
    setComposedLines([]);
    setShippingAddress(EMPTY_SHIPPING_ADDRESS);
    setSelectedShippingMethodId(null);
    selectedMethodKeyRef.current = null;
    setSelectedShippingAddressId(null);
    setIncludeShipping(true);
    setIncludeTax(true);
    taxMutation.reset();
    createMutation.reset();
    setSendCopyToSelf(false);
    setSendToOthers(false);
    setEmailRecipients([]);
    setEmailMessage("");
    setCurrencyId(null);
    shippingMethodsMutation.reset();
  }, [createMutation, shippingMethodsMutation, taxMutation]);


  const handleSelectShippingAddress = useCallback((address: QuoteAddressOption | null) => {
    setSelectedShippingAddressId(address?.id ?? null);
    const draft: ShippingAddressDraft = address
      ? {
          street: address.street,
          city: address.city,
          stateProvince: address.state,
          postalCode: address.postalCode,
          country: address.country || "US",
        }
      : EMPTY_SHIPPING_ADDRESS;
    setShippingAddress(draft);
    // Drop the prior address's methods and pick immediately; the reactive effect below re-fetches for
    // the new address (and re-fetches on later line edits), so there is no separate "Check methods"
    // action. A method must be re-picked for the new address rather than silently carried over.
    setSelectedShippingMethodId(null);
    selectedMethodKeyRef.current = null;
    shippingMethodsMutation.reset();
  }, [shippingMethodsMutation]);

  // Unchecking "Include Shipping" clears any ship-to + method selection so no freight is carried
  // onto the quote; re-checking simply reveals the (now empty) picker again.
  const handleIncludeShippingChange = useCallback((checked: boolean) => {
    setIncludeShipping(checked);
    if (!checked) {
      setSelectedShippingMethodId(null);
      selectedMethodKeyRef.current = null;
      handleSelectShippingAddress(null);
    }
  }, [handleSelectShippingAddress]);

  const handleStorefrontChange = useCallback((orgUnitIdValue: number) => {
    setSelectedOrgUnitId(orgUnitIdValue);
    setSelectedShippingAddressId(null);
    setShippingAddress(EMPTY_SHIPPING_ADDRESS);
    setSelectedShippingMethodId(null);
    selectedMethodKeyRef.current = null;
    shippingMethodsMutation.reset();
  }, [shippingMethodsMutation]);

  // Excluded lines (quantity 0 in the reversible-exclude flow) stay visible in the table but must not
  // affect pricing, tax, shipping, the create payload, or the ability to save.
  const activeLines = composedLines.filter((l) => l.quantity > 0);
  const activeLineCount = activeLines.length;

  const subtotal = activeLines.reduce((sum, l) => sum + (l.product.unitPrice ?? 0) * l.quantity, 0);
  const totalQuantity = activeLines.reduce((sum, l) => sum + l.quantity, 0);

  // Signature of the priced lines so the shipping/tax effects fire only on a real product/quantity
  // change, not on unrelated re-renders. The cart line keeps fabricated quote lines (product 0) distinct.
  const activeLineSignature = activeLines
    .map(
      (l) =>
        `${l.product.productId}:${l.product.sourceCartLineNumber ?? ""}:${l.product.sourceQuoteItemDescriptor ?? ""}:${l.quantity}`,
    )
    .join(",");

  const shippingMethods = shippingMethodsMutation.data ?? [];
  const selectedShippingMethod =
    selectedShippingMethodId != null
      ? shippingMethods.find((m) => m.id === selectedShippingMethodId)
      : undefined;
  const selectedFreight = selectedShippingMethod?.freight;

  // Only send "others" fields when that opt-in is checked; email-on-create fires if either opt-in
  // will produce a recipient (self and/or others).
  const parsedRecipients = sendToOthers ? emailRecipients : [];
  const emailOnCreate = sendCopyToSelf || parsedRecipients.length > 0;

  const shipTo =
    shippingAddress.street.trim() &&
    shippingAddress.city.trim() &&
    shippingAddress.stateProvince.trim() &&
    shippingAddress.postalCode.trim() &&
    shippingAddress.country.trim()
      ? {
          street: shippingAddress.street.trim(),
          city: shippingAddress.city.trim(),
          stateProvince: shippingAddress.stateProvince.trim(),
          postalCode: shippingAddress.postalCode.trim(),
          country: shippingAddress.country.trim(),
        }
      : undefined;

  const shippingOrgUnitId = typeof selectedOrgUnitId === "number" ? selectedOrgUnitId : null;
  // Previews price and label lines from the session storefront; create re-prices them for the selected one.
  const isOtherStorefrontSelected =
    shippingOrgUnitId != null && orgUnitId != null && shippingOrgUnitId !== orgUnitId;
  // Block create while a freight or tax preview is in flight so a quote can never be saved with a
  // stale (or missing) shipping cost / tax amount that is about to change. A failed tax preview with tax
  // still on blocks it too: the server would calculate tax anyway, so the user must retry or turn tax off,
  // as the "Tax could not be estimated" message says.
  const taxPreviewFailed =
    includeTax && includeShipping && selectedShippingMethodId != null && taxMutation.isError;
  const canCreate =
    name.trim().length > 0 &&
    activeLineCount > 0 &&
    !createMutation.isPending &&
    !shippingMethodsMutation.isPending &&
    !taxMutation.isPending &&
    !taxPreviewFailed;

  // Keep shipping methods in sync with the current ship-to address and priced lines: any product or
  // quantity edit re-fetches freight for the new weight instead of blanking the picker, and clears
  // the methods when there is nothing to price so no stale freight (and therefore no stale tax) can
  // linger.
  useEffect(() => {
    if (!includeShipping || !shipTo || activeLines.length === 0 || subtotal <= 0) {
      shippingMethodsMutation.reset();
      setSelectedShippingMethodId(null);
      selectedMethodKeyRef.current = null;
      return;
    }
    shippingMethodsMutation.mutate({
      street: shipTo.street,
      city: shipTo.city,
      state: shipTo.stateProvince,
      postalCode: shipTo.postalCode,
      countryCode: shipTo.country,
      items: activeLines.map((l) => ({
        productDescriptor: String(l.product.productId),
        productClass: l.product.productClass,
        unitPrice: l.product.unitPrice ?? 0,
        quantity: l.quantity,
        sourceCartLineNumber: l.product.sourceCartLineNumber,
        sourceQuoteItemDescriptor: l.product.sourceQuoteItemDescriptor,
      })),
    });
    // Keyed on the primitive shipping inputs (not the stable mutation object) so it fires only on a
    // real address or line change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    includeShipping,
    shippingAddress.street,
    shippingAddress.city,
    shippingAddress.stateProvince,
    shippingAddress.postalCode,
    shippingAddress.country,
    activeLineSignature,
  ]);

  // Tax opt-in (SPU-454): tax is on by default whenever shipping is on; the effect below re-estimates
  // reactively, so the toggle only flips intent.
  const handleToggleTax = useCallback((next: boolean) => {
    setIncludeTax(next);
  }, []);

  // Estimates tax for the current inputs. Shared by the reactive effect below and the retry button.
  const requestTaxEstimate = () => {
    if (!(includeTax && includeShipping && shipTo && selectedShippingMethod)) {
      return;
    }
    taxMutation.mutate({
      street: shipTo.street,
      city: shipTo.city,
      state: shipTo.stateProvince,
      postalCode: shipTo.postalCode,
      countryCode: shipTo.country,
      freight: selectedFreight ?? 0,
      items: activeLines.map((l) => ({
        productDescriptor: String(l.product.productId),
        productClass: l.product.productClass,
        unitPrice: l.product.unitPrice ?? 0,
        quantity: l.quantity,
        sourceCartLineNumber: l.product.sourceCartLineNumber,
        sourceQuoteItemDescriptor: l.product.sourceQuoteItemDescriptor,
      })),
    });
  };

  // Re-estimate whenever opted in with every required input present; clear it otherwise (opted out,
  // shipping off, or nothing to price).
  useEffect(() => {
    if (includeTax && includeShipping && shipTo && selectedShippingMethod) {
      requestTaxEstimate();
    } else {
      taxMutation.reset();
    }
    // Keyed on the primitive tax inputs (not the stable mutation object) so it fires only on a real change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    includeTax,
    includeShipping,
    selectedShippingMethodId,
    selectedFreight,
    shippingAddress.street,
    shippingAddress.city,
    shippingAddress.stateProvince,
    shippingAddress.postalCode,
    shippingAddress.country,
    composedLines,
  ]);

  // Only surface tax on the quote when the user opted in and the estimate resolved — and only while
  // the shipping context that tax was computed for still holds. Gating on `includeShipping` + a live
  // selected method means a tax result can never leak into the summary/create payload after shipping
  // is turned off or the method is cleared, independent of mutation-reset timing.
  const taxAmount =
    includeTax && includeShipping && selectedShippingMethodId != null && taxMutation.isSuccess
      ? taxMutation.data
      : undefined;

  // Summary indicators: freight is (re)loading, and tax is (re)loading — including the window where a
  // freight re-preview is in flight for an already-picked method, since tax will re-estimate right
  // after it resolves.
  const shippingCalculating = shippingMethodsMutation.isPending;
  const taxCalculating =
    taxMutation.isPending ||
    (includeTax && selectedShippingMethodId != null && shippingMethodsMutation.isPending);

  const handleCreate = useCallback(
    (onSuccess: (quoteId: number) => void) => {
      createMutation.mutate(
        {
          name: name.trim(),
          orgUnitId: selectedOrgUnitId || undefined,
          description: description.trim() || undefined,
          items: composedLines
            .filter((l) => l.quantity > 0)
            .map((l) => ({
              productId: l.product.productId,
              quantity: l.quantity,
              sourceCartLineNumber: l.product.sourceCartLineNumber,
              sourceQuoteItemDescriptor: l.product.sourceQuoteItemDescriptor,
            })),
          shipTo,
          // The server calculates freight and tax and rejects a method it does not offer; only the
          // selection and the tax opt-in are sent.
          shippingMethodId: selectedShippingMethodId ?? undefined,
          includeTax: includeTax && includeShipping && selectedShippingMethodId != null,
          emailOnCreate,
          emailRecipients: parsedRecipients,
          emailMessage: sendToOthers ? emailMessage.trim() || undefined : undefined,
          sendCopyToSelf,
          currencyId: currencyId ?? undefined,
        },
        { onSuccess: (result) => {
            // quoteId is optional in the generated client; only navigate on a real id so we never
            // route to /quotes/undefined when the create succeeds without returning one.
            if (result.quoteId != null) {
              onSuccess(result.quoteId);
            }
          } },
      );
    },
    [
      createMutation,
      name,
      selectedOrgUnitId,
      description,
      composedLines,
      shipTo,
      selectedShippingMethodId,
      includeTax,
      includeShipping,
      emailOnCreate,
      parsedRecipients,
      emailMessage,
      sendToOthers,
      sendCopyToSelf,
      currencyId,
    ],
  );

  return {
    // identity / config
    storefronts,
    storefrontsLoading,
    selectedOrgUnitId,
    shippingOrgUnitId,
    isOtherStorefrontSelected,
    createQuotesEnabled: Boolean(createQuotesEnabled),
    // lines
    lines: composedLines,
    addLine,
    removeLine,
    setQuantity,
    seedLines,
    resetComposer,
    // details
    name,
    setName,
    description,
    setDescription,
    // shipping
    shippingAddress,
    selectedShippingAddressId,
    selectedShippingMethodId,
    setSelectedShippingMethodId: handleSelectShippingMethod,
    includeShipping,
    handleIncludeShippingChange,
    handleSelectShippingAddress,
    handleStorefrontChange,
    shippingMethodsMutation,
    // tax
    includeTax,
    handleToggleTax,
    taxMutation,
    taxAmount,
    retryTax: requestTaxEstimate,
    // email
    userEmail,
    sendCopyToSelf,
    setSendCopyToSelf,
    sendToOthers,
    setSendToOthers,
    emailRecipients,
    setEmailRecipients,
    emailMessage,
    setEmailMessage,
    currencyId,
    setCurrencyId,
    // derived
    subtotal,
    totalQuantity,
    activeLineCount,
    selectedFreight,
    shippingCalculating,
    taxCalculating,
    taxPreviewFailed,
    canCreate,
    // create
    createMutation,
    handleCreate,
  };
}
